import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageSizing } from 'components/Layout/PageContentWidth';
import { useTranslation } from 'react-i18next';
import Select from 'components/Inputs/Select';
import MultiSelect from 'components/Inputs/MultiSelect';
import Row from 'components/Wizards/CustomObject/components/Row';
import KizenTypography from 'app/kizentypo';
import { getToastConfig } from 'components/Wizards/CustomObject/utilities';
import CustomObjectsService from 'services/CustomObjectsService';
import NewItemButton from 'components/Modals/Common/NewItemButton';
import { isEqual } from 'lodash';
import { RowItem } from './RowItem';
import whichServiceToUse from 'services/utils';
import useField from 'hooks/useField';
import {
  createRelatedObjectsStepState,
  DEFAULT_RELATION_PROPS,
  getAllModelOptions,
  getDefaultFields,
  getRelatedObjectTypeOptions,
  getRelationKey,
  syncSelfRelatedField,
} from './utility';
import { useToast, toastVariant } from 'components/ToastProvider';
import { useSelector } from 'react-redux';
import FieldService from 'services/FieldService';
import { useCustomObjectWizard } from 'components/Wizards/CustomObject/CustomObjectWizardContext';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import {
  PrimaryWrapper,
  ReverseWrapper,
  StyledBuilderCard,
  StyledLoader,
  StyledNotice,
  StyledSection,
  AssociationHeader,
} from './styled';
import { EMPTY_ARRAY, isContact } from 'utility/fieldHelpers';
import useFormValidation from 'hooks/useFormValidation';
import { saveStep } from 'components/Wizards/CustomObject/utilities';
import ChunkRender from 'components/ChunkRender';
import { RELATED_OBJECTS_STEP_KEY } from '../../constants';
import { getOriginalError } from 'services/AxiosService';
import { getBusinessClientObject } from 'store/authentication/selectors';
import { useAssociatedSource } from './useAssociatedSource';
import ConfirmationModal from 'components/Modals/presets/ConfirmationModal';
import { ASSOCIATION_SOURCE_TYPES } from 'components/Wizards/CustomObject/constants';
import { CUSTOM_OBJECTS } from 'queries/query-keys';
import { useQuery } from '@tanstack/react-query';
import { getErrorMessage } from 'hooks/useErrors';
import { useQueryClient } from '@tanstack/react-query';

export default function RelatedObjects({
  label,
  objectContainsWorkflow,
  isCreateMode,
  errors,
  setDirty,
  model: modelProp,
  activeStep,
  saving,
  ...props
}) {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const {
    preSaveFn: setPreSaveCallBack,
    isContactNeedsToBeAdded,
    setIsContactNeedsToBeAdded,
  } = useCustomObjectWizard();
  const queryClient = useQueryClient();

  const access = useSelector((s) => s.authentication.access);
  const clientObject = useSelector(getBusinessClientObject);
  const clientObjectId = clientObject?.id;

  const [serviceInUse, isContactObject, objectId] = useMemo(
    () => [whichServiceToUse(modelProp), isContact(modelProp), modelProp?.id],
    [modelProp]
  );

  //////////////////////////////// queries
  const { data: model, isLoading: modelLoading } = useQuery({
    queryKey: CUSTOM_OBJECTS.MODEL(objectId),
    queryFn: async () => FieldService.getModel({ id: objectId }),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: Boolean(objectId),
  });

  const {
    data: fullListOfCustomObjects = EMPTY_ARRAY,
    isLoading: fullListOfCustomObjectsLoading,
  } = useQuery({
    queryKey: [...CUSTOM_OBJECTS.LIST, FORCE_ALL_RECORDS_SIZE, 'all'],
    queryFn: async () => {
      const { results } = await CustomObjectsService.getCustomObjectList({
        size: FORCE_ALL_RECORDS_SIZE,
        customOnly: false,
      });
      return results;
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const {
    data: objectFieldsRawData = EMPTY_ARRAY,
    isLoading: objectFieldsRawLoading,
  } = useQuery({
    queryKey: CUSTOM_OBJECTS.FIELDS_UNRESTRICTED(objectId),
    queryFn: () => serviceInUse.getObjectFieldsWithoutRestrictions(objectId),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: Boolean(objectId),
  });

  const {
    data: categorizedFields = EMPTY_ARRAY,
    isLoading: categorizedFieldsLoading,
  } = useQuery({
    queryKey: [
      ...CUSTOM_OBJECTS.FIELDS_CATEGORIZED('contacts'),
      'includeVisible',
      'settingsRequest',
    ],
    queryFn: () =>
      FieldService.getCategorizedFields({
        for: 'contacts',
        includeVisible: true,
        settingsRequest: true,
      }),
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: Boolean(objectId && isContactNeedsToBeAdded),
  });

  const loading =
    modelLoading ||
    fullListOfCustomObjectsLoading ||
    objectFieldsRawLoading ||
    categorizedFieldsLoading;

  //////////////////////////////// state and memoized values
  const [
    relatedTypeOtions,
    directRelatedTypeOtions,
    reverseRelatedTypeOptions,
  ] = useMemo(() => {
    const relatedTypeOptions = getRelatedObjectTypeOptions(t);

    return [
      relatedTypeOptions,
      relatedTypeOptions.slice(0, 2),
      relatedTypeOptions.slice(2),
    ];
  }, [t]);

  const { models, contactCategories } = useMemo(() => {
    const data = fullListOfCustomObjects || EMPTY_ARRAY;
    const contactCategories = categorizedFields || EMPTY_ARRAY;

    const models = data.filter((object) => {
      const objectAccess = access.custom_objects?.custom_object_entities ?? {};
      const contactAccess = access.sections?.contacts_section?.view ?? false;

      if (object.id === clientObjectId) {
        return contactAccess;
      }

      return objectAccess[object.id]?.enabled;
    });

    return { models, contactCategories };
  }, [access, categorizedFields, clientObjectId, fullListOfCustomObjects]);

  const [objectFields, setObjectFields] = useField(
    objectFieldsRawData || EMPTY_ARRAY
  );
  const [fieldsToBeDeleted, setFieldsToBeDeleted] = useState({});

  const [relatedObjectsAll, fieldsWithSuppressedRelationIds] = useMemo(() => {
    const relatedObjects = model.relatedObjects.reduce(
      (acc, { fieldId, ...rest }) => {
        const field = objectFields?.find(({ id }) => fieldId === id);

        if (field) {
          const associationSource =
            models.find(({ id }) => rest.relatedObject === id)
              ?.associationSource || ASSOCIATION_SOURCE_TYPES.direct;

          acc.push({
            fieldId,
            ...rest,
            field,
            associationSource,
          });
        }
        return acc;
      },
      []
    );

    // adding contact as pre-populated primary relationships object
    const relatedContactObject = relatedObjects.find(isContact);
    if (!relatedContactObject && isContactNeedsToBeAdded) {
      const contact = models.find(isContact);
      const primaryTypeOption = relatedTypeOtions[0];

      relatedObjects.push({
        entityName: t('Contact'),
        fieldId: undefined,
        isNew: true,
        fetchUrl: 'client',
        relatedObject: contact?.id,
        relationType: primaryTypeOption.value,
        isPopulatedContact: true,
        field: {
          displayName: primaryTypeOption.name('Contact'),
          relation: {
            relatedName: primaryTypeOption.reverseName(model.entityName),
            rollupTimeline: false,
            inverseRelationRollupTimeline: false,
            rollupLeadsources: false,
            inverseRelationRollupLeadsources: false,
          },
        },
        categories: contactCategories,
      });
    }

    return [
      relatedObjects,
      objectFields?.reduce((acc, { id, relation }) => {
        if (relation?.inverseRelationSuppressed) {
          acc[id] = true;
        }
        return acc;
      }, {}),
    ];
  }, [
    model.relatedObjects,
    model.entityName,
    models,
    t,
    isContactNeedsToBeAdded,
    objectFields,
    contactCategories,
    relatedTypeOtions,
  ]);

  const [values, setValues] = useField(() => {
    return createRelatedObjectsStepState({
      ...model,
      relatedObjects: relatedObjectsAll,
    });
  }, [model, relatedObjectsAll]);

  const { relatedObjects, relatedObjectsReverse } = values;

  const [allModelOptions, allModelsLookup] = useMemo(() => {
    const options = getAllModelOptions(models);
    return [
      options,
      options.reduce((collect, model) => {
        collect[model.value] = model;
        return collect;
      }, {}),
    ];
  }, [models]);

  //////////////////////////////// hooks
  const {
    validateFormState,
    validationProps,
    handleInputChange,
    setValidationState,
    firstFieldWithErrorRef,
  } = useFormValidation({
    setFormDirty: setDirty,
    formState: { ...values },
    setFormState: setValues,
  });

  const {
    handleChangeAssociatedSource,
    associatedSourceOptions,
    associatedSourceObjectsOptions,
    handleChangeAssociatedSourceObjects,
    associatedSourceObjectsFieldOptions,
    handleChangeAssociatedSourceFields,
    associatedWarnings,
    confirmChangeModalProps,
    associationSourceFieldsValue,
    associationSourceFieldsError,
    associatedKey,
    validateAssociationFields,
    handleAssociationFieldsErrors,
  } = useAssociatedSource({
    values,
    relatedObjects: relatedObjectsAll,
    handleInputChange,
    modelId: objectId,
    setValidationState,
    firstFieldWithErrorRef,
  });

  //////////////////////////////// handlers

  const setValueHandler = useCallback(
    (data) => {
      setValues(data);
      const newObjects = data.relatedObjects.filter(({ isNew }) => isNew);
      const newObjectsReverse = data.relatedObjectsReverse.filter(
        ({ isNew }) => isNew
      );

      if (newObjects.length || newObjectsReverse.length) {
        setDirty(true);
      }
    },
    [setDirty, setValues]
  );

  const preSaveCallBack = useCallback(async () => {
    if (!validateFormState(t) || !validateAssociationFields()) {
      return null;
    }

    try {
      const {
        relatedObjects,
        relatedObjectsReverse,
        associationSource,
        associationSourceFields,
      } = values;
      const allObjects = [...relatedObjects, ...relatedObjectsReverse];

      const newObjectsWithoutRelatedObject = allObjects.filter(
        ({ isNew, relatedObject }) => isNew && !relatedObject
      );

      if (newObjectsWithoutRelatedObject.length) {
        throw new Error(
          t(
            'The Custom Object was not updated. At least one of the Related Objects is missing.'
          )
        );
      }

      setFieldsToBeDeleted({});

      const deletedFields = await Promise.all(
        Object.values(fieldsToBeDeleted).map(async ({ fieldId }) => {
          try {
            return await serviceInUse.deleteObjectField(objectId, fieldId);
          } catch {
            return null;
          }
        })
      );

      if (deletedFields.includes(null)) {
        showToast({
          variant: toastVariant.FAILURE,
          message: t('Some of the relations were not deleted.'),
        });
      }

      const categories = await serviceInUse.getObjectCategories(
        objectId,
        isContactObject
      );

      const [relatedPayloads, reversePayloads] = [
        relatedObjects,
        relatedObjectsReverse,
      ].map((objects) =>
        objects.map((object) => {
          if (object.isNew && object.relatedObject) {
            const dictionary = relatedTypeOtions.find(
              ({ value: valueId }) => valueId === object.relationType
            );

            const relatedModel = allModelsLookup[object.relatedObject];

            const payload = [
              objectId,
              {
                model: objectId,
                category: categories[0].id,
                displayName:
                  object.field?.displayName ||
                  dictionary.name(relatedModel.entityName),
                relation: {
                  relatedCategory: object.categories[0].id,
                  relatedName:
                    object.field.relation?.relatedName ||
                    dictionary.reverseName(model.entityName),
                  relatedObject: object.relatedObject,
                  relationType: object.relationType,
                  rollupTimeline: object.field.relation.rollupTimeline,
                  inverseRelationRollupTimeline:
                    object.field.relation.inverseRelationRollupTimeline,
                  rollupLeadsources: object.field.relation.rollupLeadsources,
                  inverseRelationRollupLeadsources:
                    object.field.relation.inverseRelationRollupLeadsources,
                  inverseRelationSuppressed:
                    object.field.relation.inverseRelationSuppressed,
                },
                ...DEFAULT_RELATION_PROPS,
              },
            ];
            return { id: 'create', payload };
          }
          if (
            !objectFields.some((field) =>
              isEqual(
                { displayName: field.displayName, relation: field.relation },
                {
                  displayName: object?.field?.displayName,
                  relation: object?.field?.relation,
                }
              )
            )
          ) {
            const payload = [
              objectId,
              object.fieldId,
              {
                display_name: object.field.displayName,
                relation: {
                  ...object.field.relation,
                  // if the relation is suppressed, we need to remove the inverse_relation_suppressed
                  ...(object.field.isSuppressed
                    ? { inverse_relation_suppressed: undefined }
                    : {}),
                },
              },
            ];

            return { id: 'update', payload };
          }
          return {};
        })
      );

      const [relatedResponses, reverseResponses] = await Promise.all(
        [relatedPayloads, reversePayloads].map((payloads) => {
          // We should have not more than 16 concurent requests at a time, so we chunk them into groups of 16.
          const chunks = payloads.reduce((collect, payload, index) => {
            const chunkIndex = Math.floor(index / 16);
            collect[chunkIndex] = collect[chunkIndex] || [];
            collect[chunkIndex].push(payload);
            return collect;
          }, []);
          const loadChunks = async (chunks) => {
            const responses = [];
            for (const chunk of chunks) {
              const chunkResponses = await Promise.all(
                chunk.map(({ id, payload }) => {
                  switch (id) {
                    case 'create': {
                      return serviceInUse.createObjectField(...payload);
                    }
                    case 'update': {
                      return serviceInUse.patchObjectField(...payload);
                    }
                    default:
                      return null;
                  }
                })
              );
              responses.push(...chunkResponses);
            }
            return responses;
          };
          return loadChunks(chunks);
        })
      );

      const modelResponse = await saveStep(
        {
          associationSource,
          associationSourceFields: (associationSourceFields || []).map(
            (id) => ({ id })
          ),
        },
        model
      );
      queryClient.setQueryData(CUSTOM_OBJECTS.MODEL(objectId), modelResponse);

      if (relatedResponses.some(Boolean) || reverseResponses.some(Boolean)) {
        setValues({
          ...values,
          relatedObjects: relatedObjects.map((object, index) => ({
            ...object,
            isNew: false,
            fieldId: relatedResponses[index]?.id || object?.field?.id,
            field: relatedResponses[index] || object?.field,
          })),
          relatedObjectsReverse: relatedObjectsReverse.map((object, index) => ({
            ...object,
            isNew: false,
            fieldId: reverseResponses[index]?.id || object?.field?.id,
            field: reverseResponses[index] || object?.field,
          })),
        });

        const updatedLookup = [...relatedResponses, ...reverseResponses].reduce(
          (collect, response) => {
            if (response) {
              collect[response.id] = response;
            }
            return collect;
          },
          {}
        );

        const existingLookup = objectFields.reduce((collect, field) => {
          collect[field.id] = field;
          return collect;
        }, {});

        setObjectFields((prev) => {
          const newFields = [...relatedResponses, ...reverseResponses].reduce(
            (collect, response) => {
              if (response && !existingLookup[response.id]) {
                collect.push(response);
              }
              return collect;
            },
            []
          );
          const updatedFields = prev.map((prevField) => {
            return {
              ...(updatedLookup[prevField.id] || prevField),
              // PATCH field response does not include the canonical_display_name
              // we might need to better fix this on BE side
              canonicalDisplayName:
                updatedLookup[prevField.id]?.canonicalDisplayName ||
                prevField.canonicalDisplayName,
            };
          });

          return updatedFields.concat(newFields);
        });
      }

      setIsContactNeedsToBeAdded(false);
      setDirty(false);
      // the toast DOM-updates can be blocked by other wizard state DOM-related updates
      setTimeout(() =>
        showToast(getToastConfig(t, !model, isContactObject).success)
      );
    } catch (error) {
      const fallbackError = t('An error occurred');
      const backendError = getOriginalError(error);

      if (
        Array.isArray(backendError?.association_source_fields) &&
        backendError.association_source_fields.some((error) => error?.id)
      ) {
        handleAssociationFieldsErrors(backendError.association_source_fields);

        return null;
      }

      showToast({
        variant: toastVariant.FAILURE,
        message: t('{{backendError}} - Please re-save your changes.', {
          backendError: getErrorMessage(backendError) || fallbackError,
        }),
      });

      return null;
    }

    return false;
  }, [
    validateFormState,
    t,
    validateAssociationFields,
    objectId,
    isContactObject,
    values,
    serviceInUse,
    model,
    setIsContactNeedsToBeAdded,
    setDirty,
    objectFields,
    allModelsLookup,
    setValues,
    setObjectFields,
    showToast,
    fieldsToBeDeleted,
    handleAssociationFieldsErrors,
    relatedTypeOtions,
    queryClient,
  ]);

  useEffect(() => {
    setPreSaveCallBack(preSaveCallBack);
  }, [preSaveCallBack, setPreSaveCallBack]);

  useEffect(() => {
    if (isContactNeedsToBeAdded) {
      setDirty(true);
    }
  }, [isContactNeedsToBeAdded, setDirty]);

  const onConfirmDelete = useCallback(
    (field) => {
      const { id, relation } = field;
      const relatedObject = relatedObjectsAll.find(
        (item) => item.fieldId === id
      );
      const relatedFieldId = relation.relatedField;

      setFieldsToBeDeleted((prev) => ({
        ...prev,
        [relatedObject.fieldId]: {
          fieldId: id,
          relatedFieldId,
        },
      }));

      const associationSourceObjects = values.associationSourceObjects.filter(
        ({ value }) => relatedObject.id !== value
      );
      const associationSourceFields = values.associationSourceFields.filter(
        (value) => id !== value && relatedFieldId !== value
      );

      handleInputChange('associationSourceObjects', associationSourceObjects);
      handleInputChange(
        'associationSourceFields',
        associationSourceFields,
        undefined,
        {
          associationSourceObjects,
          relatedObjects: relatedObjects.filter(
            ({ fieldId }) => fieldId !== relatedFieldId && fieldId !== id
          ),
          relatedObjectsReverse: relatedObjectsReverse.filter(
            ({ fieldId }) => fieldId !== relatedFieldId && fieldId !== id
          ),
        }
      );
    },
    [
      relatedObjectsAll,
      values.associationSourceObjects,
      values.associationSourceFields,
      handleInputChange,
      relatedObjects,
      relatedObjectsReverse,
    ]
  );

  const handleOnChange = useCallback(
    async (
      updated,
      index,
      key = 'relatedObjectsReverse',
      shouldUpdateCategories = false
    ) => {
      const object = models.find(({ id }) => id === updated?.relatedObject);
      let categories = [];

      if (updated?.isNew && shouldUpdateCategories) {
        categories = updated?.relatedObject
          ? updated.isContact
            ? await FieldService.getCategorizedFields({
                for: 'contacts',
                includeVisible: true,
                settingsRequest: true,
              })
            : await whichServiceToUse(object).getObjectCategories(
                updated.relatedObject
              )
          : [];
      }

      const nextValues = {
        ...values,
        [key]:
          key === 'relatedObjectsReverse'
            ? relatedObjectsReverse.map((r, i) => {
                if (i === index) {
                  return {
                    ...updated,
                    categories: shouldUpdateCategories
                      ? categories
                      : r?.categories || [],
                  };
                }
                return r;
              })
            : relatedObjects.map((r, i) => {
                if (i === index) {
                  return {
                    ...updated,
                    categories: shouldUpdateCategories
                      ? categories
                      : r?.categories || [],
                  };
                }
                return r;
              }),
      };

      if (updated.relatedObject === objectId) {
        nextValues.relatedObjects = nextValues.relatedObjects.map(
          (currentRelation) => {
            if (
              updated.fieldId === currentRelation.field.relation.relatedField
            ) {
              return syncSelfRelatedField(currentRelation, updated);
            }
            return currentRelation;
          }
        );
        nextValues.relatedObjectsReverse = nextValues.relatedObjectsReverse.map(
          (currentRelation, i) => {
            if (
              updated.fieldId === currentRelation.field.relation.relatedField
            ) {
              return syncSelfRelatedField(currentRelation, updated);
            }
            return currentRelation;
          }
        );
      }

      handleInputChange(key, nextValues[key]);

      setValueHandler(nextValues);
    },
    [
      handleInputChange,
      models,
      relatedObjects,
      relatedObjectsReverse,
      setValueHandler,
      values,
      objectId,
    ]
  );

  const handleClickRemove = useCallback(
    (type = 'relatedObjects', objects, values, index, relation) => {
      if (relation?.isPopulatedContact) {
        setIsContactNeedsToBeAdded(false);
      }
      setValueHandler({
        ...values,
        [type]: objects.filter((_, i) => i !== index),
      });
    },
    [setValueHandler, setIsContactNeedsToBeAdded]
  );

  // combine validation props for associationSourceFields
  const associationSourceFieldsValidation = validationProps(
    'associationSourceFields'
  );
  const fieldsError = useMemo(() => {
    return associationSourceFieldsValidation.validate
      ? associationSourceFieldsValidation
      : {
          ...associationSourceFieldsValidation,
          validate: associationSourceFieldsError,
        };
  }, [associationSourceFieldsError, associationSourceFieldsValidation]);

  const validationOnLoad = useRef(false);

  if (
    !validationOnLoad.current &&
    !loading &&
    activeStep.key === RELATED_OBJECTS_STEP_KEY &&
    values?.associationSource === ASSOCIATION_SOURCE_TYPES.related &&
    !associationSourceFieldsValue?.length === 0
  ) {
    setTimeout(() => {
      firstFieldWithErrorRef.current = 'associationSourceObjects';
      setValidationState((prev) => ({
        ...prev,
        associationSourceObjects: t('This field is required'),
        associationSourceFields: t('This field is required'),
      }));
    }, 10);
    validationOnLoad.current = true;
  }

  return (
    <PageSizing>
      <StyledBuilderCard {...props} label={label}>
        <AssociationHeader>
          <div>
            <Select
              key={associatedKey}
              label={t('Where does this object get its team associations?')}
              value={values?.associationSource}
              onChange={handleChangeAssociatedSource}
              options={associatedSourceOptions}
              placeholder={t('Choose Associations')}
              dataQa="related-objects-associations"
              {...validationProps('associationSource')}
            />
          </div>
          <div>
            {values?.associationSource !== ASSOCIATION_SOURCE_TYPES.direct ? (
              <MultiSelect
                label={t('Related Objects for Associations')}
                value={values?.associationSourceObjects || []}
                onChange={handleChangeAssociatedSourceObjects}
                options={associatedSourceObjectsOptions}
                placeholder={t('Choose Object(s)')}
                dataQa="related-objects-associations-objects"
                menuLeftButton={null}
                menuRightButton={null}
                required={
                  values?.associationSource === ASSOCIATION_SOURCE_TYPES.related
                }
                {...validationProps('associationSourceObjects')}
              />
            ) : null}
          </div>
          <div>
            {values?.associationSource !== ASSOCIATION_SOURCE_TYPES.direct ? (
              <MultiSelect
                label={t('Relationship Fields for Associations')}
                value={associationSourceFieldsValue || []}
                onChange={handleChangeAssociatedSourceFields}
                options={associatedSourceObjectsFieldOptions}
                placeholder={t('Choose Field(s)')}
                dataQa="related-objects-associations-fields"
                menuLeftButton={null}
                menuRightButton={null}
                required={
                  values?.associationSource ===
                    ASSOCIATION_SOURCE_TYPES.related ||
                  values?.associationSourceObjects?.length > 0
                }
                {...fieldsError}
              />
            ) : null}
          </div>
        </AssociationHeader>
        <StyledSection label={t('This Object has Primary Relationships')}>
          <StyledNotice noRows={!relatedObjects.length}>
            <KizenTypography data-qa={`no-items-notification`}>
              {t(
                'These are single select relationships between your data. For example, we recommend using a primary relationship for deals with a single point of contact.'
              )}
            </KizenTypography>
          </StyledNotice>
          {loading ? (
            <StyledLoader loading />
          ) : (
            <Row>
              {relatedObjects.length ? (
                <PrimaryWrapper>
                  <ChunkRender chunkSize={10}>
                    {relatedObjects.map((relation, index) => {
                      return (
                        <RowItem
                          index={index}
                          dataQa={`related-objects-line-item-${index}`}
                          key={getRelationKey(relation, index)}
                          value={relation}
                          allModelOptions={allModelOptions}
                          errors={errors}
                          objectFields={objectFields}
                          // Editable if this relation doesn't exist
                          editable={!objectId || relation.isNew}
                          relation={'relatedObjects'}
                          entityName={model.entityName}
                          allRelationTypeOptions={directRelatedTypeOtions}
                          onChange={(data, shouldUpdateCategories) =>
                            handleOnChange(
                              data,
                              index,
                              'relatedObjects',
                              shouldUpdateCategories
                            )
                          }
                          onClickRemove={() =>
                            handleClickRemove(
                              'relatedObjects',
                              relatedObjects,
                              values,
                              index,
                              relation
                            )
                          }
                          onConfirmDelete={onConfirmDelete}
                          validationProps={validationProps}
                          relationshipSuppressed={
                            fieldsWithSuppressedRelationIds[relation.fieldId]
                          }
                        />
                      );
                    })}
                  </ChunkRender>
                </PrimaryWrapper>
              ) : null}
            </Row>
          )}
          <NewItemButton
            onClick={() =>
              setValueHandler({
                ...values,
                relatedObjects: [
                  ...values.relatedObjects,
                  getDefaultFields('primary', t),
                ],
              })
            }
            data-qa={'new-primary-row-button'}
          >
            {`+ ${t('Add Related Object')}`}
          </NewItemButton>
        </StyledSection>
        <StyledSection label={t('Additional Relationships')}>
          <StyledNotice noRows={!relatedObjectsReverse.length}>
            <KizenTypography data-qa={`no-items-notification`}>
              {t(
                'These will create multi-select fields to represent multiple associations between your data. For example, we recommend using an additional relationship for deals that may have additional points of contact.'
              )}
            </KizenTypography>
          </StyledNotice>
          {loading ? (
            <StyledLoader loading />
          ) : (
            <Row>
              {relatedObjectsReverse.length ? (
                <ReverseWrapper>
                  <ChunkRender chunkSize={10}>
                    {relatedObjectsReverse.map((relation, index) => {
                      return (
                        <RowItem
                          index={index}
                          dataQa={`reverse-related-objects-line-item-${index}`}
                          key={getRelationKey(relation, index)}
                          value={relation}
                          allModelOptions={allModelOptions}
                          errors={errors}
                          objectFields={objectFields}
                          editable={!objectId || relation.isNew}
                          relation={'relatedObjectsReverse'}
                          entityName={model.entityName}
                          allRelationTypeOptions={reverseRelatedTypeOptions}
                          onChange={(data, shouldUpdateCategories) =>
                            handleOnChange(
                              data,
                              index,
                              'relatedObjectsReverse',
                              shouldUpdateCategories
                            )
                          }
                          onClickRemove={() =>
                            handleClickRemove(
                              'relatedObjectsReverse',
                              relatedObjectsReverse,
                              values,
                              index,
                              relation
                            )
                          }
                          onConfirmDelete={onConfirmDelete}
                          validationProps={validationProps}
                          relationshipSuppressed={
                            fieldsWithSuppressedRelationIds[relation.fieldId]
                          }
                        />
                      );
                    })}
                  </ChunkRender>
                </ReverseWrapper>
              ) : null}
            </Row>
          )}
          <NewItemButton
            onClick={() => {
              setValueHandler({
                ...values,
                relatedObjectsReverse: [
                  ...values.relatedObjectsReverse,
                  getDefaultFields('additional', t),
                ],
              });
            }}
            data-qa={'new-reverse-row-button'}
          >
            {`+ ${t('Add Related Object')}`}
          </NewItemButton>
        </StyledSection>
        <ConfirmationModal
          enforceFocus={false}
          heading={t('Please Confirm Change')}
          buttonText={t('Confirm Change')}
          {...confirmChangeModalProps}
        >
          {associatedWarnings}
        </ConfirmationModal>
      </StyledBuilderCard>
    </PageSizing>
  );
}
